import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import api from '../api/client';
import { Client } from '../types';
import ProductManager from './ProductManager';
import ClientBulkUpload from './ClientBulkUpload';

const ClientManager: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [newClientName, setNewClientName] = useState('');
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showBulkCreate, setShowBulkCreate] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await api.getClients();
      // Sort clients by the number of products they have in descending order
      const sortedClients = [...response].sort((a, b) => {
        return (b.products?.length || 0) - (a.products?.length || 0);
      });
      setClients(sortedClients);
    } catch (err) {
      setError('Failed to load clients');
    }
  };

  const handleAddClient = async () => {
    if (!newClientName.trim()) return;
    try {
      await api.createClient({ name: newClientName });
      setSuccess('Client added successfully');
      setNewClientName('');
      loadClients();
    } catch (err) {
      setError('Failed to add client');
    }
  };

  const handleEditClient = async () => {
    if (!editingClient) return;
    try {
      await api.updateClient(editingClient.id, { name: editingClient.name });
      setSuccess('Client updated successfully');
      setEditingClient(null);
      loadClients();
    } catch (err) {
      setError('Failed to update client');
    }
  };

  const handleDeleteClient = async (clientId: number) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await api.deleteClient(clientId);
      setSuccess('Client deleted successfully');
      loadClients();
    } catch (err) {
      setError('Failed to delete client');
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Client Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="New Client Name"
            value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClient}
          >
            Add Client
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => setShowBulkCreate(true)}
          >
            Bulk Add
          </Button>
        </Box>

        <TextField
          fullWidth
          label="Search Clients"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ mb: 2 }}
        />

        <List>
          {filteredClients.map((client) => (
            <ListItem
              key={client.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => setSelectedClient(client)}
            >
              <ListItemText
                primary={client.name}
                secondary={`${client.products?.length || 0} products`}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="edit"
                  onClick={() => setEditingClient(client)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteClient(client.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Dialog open={!!editingClient} onClose={() => setEditingClient(null)}>
        <DialogTitle>Edit Client</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Client Name"
            fullWidth
            value={editingClient?.name || ''}
            onChange={(e) =>
              setEditingClient(
                editingClient
                  ? { ...editingClient, name: e.target.value }
                  : null
              )
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingClient(null)}>Cancel</Button>
          <Button onClick={handleEditClient} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ClientBulkUpload
        open={showBulkCreate}
        onClose={() => setShowBulkCreate(false)}
        onSuccess={() => {
          setShowBulkCreate(false);
          loadClients();
          setSuccess('Clients uploaded successfully');
        }}
      />

      {selectedClient && (
        <ProductManager
          clientId={selectedClient.id}
          clientName={selectedClient.name}
          open={true}
          onClose={() => setSelectedClient(null)}
        />
      )}
    </Box>
  );
};

export default ClientManager;
